/**
 * Promote Try Temp Attachments
 *
 * Uploads temporary /try draft attachments into the authenticated workspace.
 *
 * Primary responsibilities:
 * - Persist try-mode text files after login
 * - Clear temporary try attachment state once promoted
 *
 * Dependencies:
 * - createFile
 * - try-temp-attachments helpers
 */

import { createFile } from '@/db/files'
import type { Tables } from '@/supabase/types'
import type { ChatFile } from '@/types/chat-file'
import type { MessageImage } from '@/types/images/message-image'
import type { ChatSettings } from '@/types/chat'
import {
  clearAllTryTempFileContents,
  clearTryTempFileContent,
  isTryTempFileId
} from '@/lib/chat/try-temp-attachments'
import { safeConsole } from '@/lib/utils/safeConsole'

type PromoteTryTempAttachmentsArgs = {
  profile: Tables<'profiles'>
  workspaceId: string
  chatSettings: ChatSettings
  newMessageFiles: ChatFile[]
  newMessageImages: MessageImage[]
}

export async function promoteTryTempAttachments({
  profile,
  workspaceId,
  chatSettings,
  newMessageFiles,
  newMessageImages
}: PromoteTryTempAttachmentsArgs): Promise<{
  promotedFiles: ChatFile[]
  promotedImages: MessageImage[]
}> {
  const promotedFiles: ChatFile[] = []

  for (const draftFile of newMessageFiles) {
    const isTemporary = draftFile.isTemporary || isTryTempFileId(draftFile.id)

    if (!isTemporary) {
      promotedFiles.push(draftFile)
      continue
    }

    if (!draftFile.file) {
      // Temp draft without a File blob cannot be uploaded; drop it cleanly.
      clearTryTempFileContent(draftFile.id)
      continue
    }

    try {
      const createdFile = await createFile(
        draftFile.file,
        {
          user_id: profile.user_id,
          description: 'Promoted from try chat',
          file_path: '',
          name: draftFile.name,
          size: draftFile.file.size,
          tokens: 0,
          type: draftFile.type
        },
        workspaceId,
        chatSettings.embeddingsProvider,
        draftFile.tempContent
      )

      clearTryTempFileContent(draftFile.id)
      promotedFiles.push({
        id: createdFile.id,
        name: createdFile.name,
        type: createdFile.type,
        file: draftFile.file
      })
    } catch (error) {
      safeConsole.warn('Failed to promote try temp file on login', error)
      // Keep the local draft so the user can still send it after login.
      promotedFiles.push(draftFile)
    }
  }

  // Composer images stay as local drafts; authenticated send uploads them
  // through the normal message-image persistence path.
  clearAllTryTempFileContents()

  return {
    promotedFiles,
    promotedImages: [...newMessageImages]
  }
}
